import { useEffect, useState } from "react";
import jwtDecode from "jwt-decode";
import useTranslator from "../../hooks/useTranslator";
import {
  Badge,
  Button,
  Icon,
  InfoList,
  Notice,
  PublicPage,
  Select,
} from "../../components/design";
import "./tenantBankVerificationRequest.css";
import { useNavigate } from "react-router-dom";
import { calculatePaymentScore, clearPaymentData } from "../../helper/common";
import ProcessPassedModal from "../onboarding/ProcessPassedModal";

const TenantBankVerificationRequest = () => {
  const { _t } = useTranslator();
  const [userToken, setUserToken] = useState({
    landlord_name: "",
    tenant_id: "",
    requested_tenant_name: "",
  });
  const [countryCodeError, setCountryCodeError] = useState("");
  const [tink_countryCode, setTink_countryCode] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);

  const navigate = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const userTokenParam = queryParameters.get("userToken");
  let account_verification_report_id = queryParameters.get(
    "account_verification_report_id"
  );
  let transaction_report_id = queryParameters.get("transaction_report_id");

  useEffect(() => {
    const decoded = jwtDecode(userTokenParam);
    setUserToken(decoded);

    if (
      userTokenParam &&
      account_verification_report_id &&
      transaction_report_id
    ) {
      calculateScore(
        userTokenParam,
        account_verification_report_id,
        transaction_report_id
      );
      return;
    }
  }, []);

  const calculateScore = async (
    userTokenParam,
    accountverificationid,
    transactionreportid
  ) => {
    account_verification_report_id = null;
    transaction_report_id = null;
    await clearPaymentData(userTokenParam);
    setShowConfirmation(true);
    calculatePaymentScore(
      userTokenParam,
      accountverificationid,
      transactionreportid
    );
  };

  const changeCountryCode = (e) => {
    setTink_countryCode(e.target.value);
  };

  const connectToBank = (e) => {
    e.preventDefault();
    if (!tink_countryCode) {
      setCountryCodeError(_t("pleaseSelectCountryCode"));
      return;
    } else {
      setCountryCodeError("");
    }
    var lang = "en_US";
    if (localStorage.getItem("i18nextLng") == "es") {
      lang = "es_ES";
    }

    let baseUrl = `https://link.tink.com/1.0/reports/create-report?client_id=${process.env.REACT_APP_TINK_CLIENT_ID}&market=${tink_countryCode}&locale=${lang}&report_types=TRANSACTION_REPORT,ACCOUNT_VERIFICATION_REPORT&refreshable_items=IDENTITY_DATA,CHECKING_ACCOUNTS,SAVING_ACCOUNTS,CHECKING_TRANSACTIONS,SAVING_TRANSACTIONS&account_dialog_type=SINGLE`;

    let tinkUrlLiveFront;

    tinkUrlLiveFront = `${baseUrl}&redirect_uri=https%3A%2F%2F${process.env.REACT_APP_URL_NAME}%2Ftenant-bank-verification?userToken=${userTokenParam}`;
    window.open(tinkUrlLiveFront, "_self");
  };

  const requesterName =
    userToken?.landlord_name ?? userToken?.requested_tenant_name;

  const countryOptions = [
    ["BE", "belgium"],
    ["DE", "germany"],
    ["DK", "denmark"],
    ["EE", "estonia"],
    ["ES", "spain"],
    ["FI", "finland"],
    ["FR", "france"],
    ["GB", "unitedKingdom"],
    ["IT", "italy"],
    ["LT", "lithuania"],
    ["LV", "latvia"],
    ["NL", "netherlands"],
    ["NO", "norway"],
    ["PL", "poland"],
    ["PT", "portugal"],
    ["SE", "sweden"],
  ].map(([value, key]) => ({ value, label: _t(key) }));

  const perks = [
    { icon: "shieldCheck", text: _t("securityAndPrivacyAreGuaranteed") },
    { icon: "checkCircle", text: _t("automaticAnalysisWithInstantResults") },
    { icon: "home", text: _t("makingRentingProcessEasy") },
    { icon: "lock", text: _t("landlordCannotAccessYourBank") },
  ];

  return (
    <>
      <PublicPage
        width="md"
        title={_t("bankVerificationRequest")}
        description={
          userToken?.landlord_name
            ? _t("aLandlordHasRequestedYouToCompleteYourBankVerification")
            : _t("aTenantHasRequestedYouToCompleteYourBankVerification")
        }
        footer={
          <>
            {_t("shouldYouHaveAnyConcernsOrQuestionsPlease")}
            <a
              href="/bank"
              onClick={(e) => {
                e.preventDefault();
                navigate("/bank");
              }}
              className="bank-request__link"
            >
              {_t("readHere")}
            </a>
            {_t("orContactUsAt")}
            <a href="mailto:info@skortorent.com" className="bank-request__link">
              info@skortorent.com.
            </a>
          </>
        }
      >
        <div className="bank-request">
          <InfoList
            items={[
              {
                label: _t("requestedBy"),
                value: requesterName && (
                  <>
                    {requesterName}{" "}
                    <Badge tone="neutral">
                      {userToken?.landlord_name ? _t("landlord") : _t("tenant")}
                    </Badge>
                  </>
                ),
              },
            ]}
          />

          <ul className="bank-request__perks">
            {perks.map((perk) => (
              <li key={perk.icon}>
                <Icon name={perk.icon} size={20} />
                <span>{perk.text}</span>
              </li>
            ))}
          </ul>

          <Notice tone="info">
            {_t("weAssureYouOfTheUtmostPrivacyAndSecurityOfYourFinancialData")}
          </Notice>

          <form className="bank-request__form" onSubmit={connectToBank}>
            <Select
              id="employment_status"
              label={_t("country")}
              placeholder={_t("selectYourCountry")}
              value={tink_countryCode}
              onChange={changeCountryCode}
              options={countryOptions}
              error={countryCodeError}
            />
            <Button
              type="submit"
              size="lg"
              block
              iconStart={<Icon name="upload" />}
            >
              {_t("connectBank")}
            </Button>
          </form>
        </div>
      </PublicPage>
      {showConfirmation && (
        <ProcessPassedModal
          showPopup={showConfirmation}
          heading={_t("bankVerificationCompleted", "new tt bank connection")}
          subheading={_t(
            "yourBankHasBeenSuccessfullyConnected",
            "new tt bank connection"
          )}
          buttonText={_t("Done")}
          handleClick={(e) => {
            navigate("/home");
          }}
        />
      )}
    </>
  );
};

export default TenantBankVerificationRequest;
